import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Send, MessageCircle, Sparkles, Building2, Calendar, Clock, MapPin, Check, Mic, Phone, Volume2, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import ContactModal from "../components/ContactModal";
import VoiceChatInput, { stopCurrentAudio } from "../components/VoiceChatInput";
import ModeChoiceButtons from "../components/ModeChoiceButtons";
import VoiceRecorder from "../components/VoiceRecorder";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BOT_AVATAR = "https://customer-assets.emergentagent.com/job_job-chatbot-1/artifacts/ab0dslhp_L%C3%A4chelndes%20Portrait%20einer%20jungen%20Frau.png";

// Render message content with bold text + klickbare Telefonnummern (auch innerhalb **fett**)
const PHONE_SPLIT_REGEX = /(\+?49[\s\-]?211[\s\-]?86943288|0211[\s\-]?86943288)/g;
const PHONE_MATCH_REGEX = /^(\+?49[\s\-]?211[\s\-]?86943288|0211[\s\-]?86943288)$/;

const linkifyPhones = (text, keyPrefix) => {
  const sub = text.split(PHONE_SPLIT_REGEX);
  return sub.map((chunk, j) => {
    if (chunk && PHONE_MATCH_REGEX.test(chunk)) {
      return (
        <span key={`${keyPrefix}-${j}`} className="inline-block align-baseline">
          <a
            href="tel:+4921186943288"
            className="text-blue-600 underline hover:text-blue-700"
            data-testid="chat-call-link"
          >
            {chunk}
          </a>
          <span className="block text-[11px] font-normal text-slate-500 mt-0.5">
            Firmenzentrale Düsseldorf · Festnetz · 7/24 Std. erreichbar
          </span>
        </span>
      );
    }
    return chunk;
  });
};

const renderMessageContent = (content) => {
  const parts = content.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    const isBold = part.startsWith('**') && part.endsWith('**');
    const text = isBold ? part.slice(2, -2) : part;
    const rendered = linkifyPhones(text, i);
    if (isBold) {
      return <strong key={i} className="font-bold">{rendered}</strong>;
    }
    return <span key={i}>{rendered}</span>;
  });
};

// Slot Selection Component
// Slot Selection Modal Component
const SlotSelectionModal = ({ slots, isOpen, onClose, onConfirm, selectedSlot, onSelect }) => {
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-violet-600 p-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-6 h-6" />
                  <h2 className="text-lg font-bold">Termin auswählen</h2>
                </div>
                <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-blue-100 mt-1">Wähle deinen Wunschtermin in Düsseldorf</p>
            </div>
            
            {/* Slots List */}
            <div className="p-4 max-h-[50vh] overflow-y-auto space-y-2">
              {slots.map((slot) => (
                <motion.button
                  key={slot.id}
                  onClick={() => onSelect(slot)}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className={`w-full flex items-center gap-3 p-4 rounded-xl transition-all ${
                    selectedSlot?.id === slot.id
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                      : 'bg-slate-50 hover:bg-blue-50 text-slate-700 border border-slate-200 hover:border-blue-300'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    selectedSlot?.id === slot.id
                      ? 'border-white bg-white'
                      : 'border-slate-300'
                  }`}>
                    {selectedSlot?.id === slot.id && (
                      <Check className="w-4 h-4 text-blue-600" />
                    )}
                  </div>
                  <div className="flex items-center gap-4 flex-1">
                    <div className="flex items-center gap-1.5">
                      <Calendar className={`w-4 h-4 ${selectedSlot?.id === slot.id ? 'text-blue-200' : 'text-blue-500'}`} />
                      <span className="font-medium">{slot.display.split(' – ')[0]}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className={`w-4 h-4 ${selectedSlot?.id === slot.id ? 'text-blue-200' : 'text-blue-500'}`} />
                      <span>{slot.time} Uhr</span>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2 mb-3 text-sm text-slate-500">
                <MapPin className="w-4 h-4" />
                <span>Standort: Düsseldorf (Hauptstelle)</span>
              </div>
              
              <Button
                onClick={() => {
                  if (selectedSlot) {
                    onConfirm();
                    onClose();
                  }
                }}
                disabled={!selectedSlot}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:from-slate-300 disabled:to-slate-400 text-white rounded-xl py-4 text-base font-semibold shadow-lg shadow-green-500/30 disabled:shadow-none"
              >
                <Check className="w-5 h-5 mr-2" />
                {selectedSlot ? 'Termin bestätigen' : 'Bitte Termin wählen'}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Button to open slot selection
const SlotSelectionButton = ({ onClick, slotsCount }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-3"
    >
      <Button
        onClick={onClick}
        className="w-full bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-600 hover:to-violet-700 text-white rounded-xl py-4 text-base font-semibold shadow-lg shadow-blue-500/30"
      >
        <Calendar className="w-5 h-5 mr-2" />
        Freie Termine anzeigen ({slotsCount} verfügbar)
      </Button>
    </motion.div>
  );
};

const TypingIndicator = () => (
  <div className="flex items-start gap-3 animate-fade-in">
    <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-md">
      <img src={BOT_AVATAR} alt="Bot" className="w-full h-full object-cover" />
    </div>
    <div className="bg-white border border-slate-100 rounded-2xl rounded-tl-sm shadow-sm px-4 py-3">
      <div className="flex gap-1.5">
        <span className="typing-dot w-2 h-2 bg-slate-400 rounded-full"></span>
        <span className="typing-dot w-2 h-2 bg-slate-400 rounded-full"></span>
        <span className="typing-dot w-2 h-2 bg-slate-400 rounded-full"></span>
      </div>
    </div>
  </div>
);

const ChatMessage = ({ message, index }) => {
  const isBot = message.role === "assistant";
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className={`flex items-start gap-3 ${isBot ? "" : "flex-row-reverse"}`}
      data-testid={`chat-message-${message.role}-${index}`}
    >
      {isBot && (
        <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 border-2 border-white shadow-md">
          <img src={BOT_AVATAR} alt="Bot" className="w-full h-full object-cover" />
        </div>
      )}
      <div
        className={`max-w-[85%] px-4 py-3 shadow-sm ${
          isBot
            ? "bg-white border border-slate-100 text-slate-800 chat-bubble-bot"
            : "bg-gradient-to-br from-blue-600 to-violet-600 text-white chat-bubble-user"
        }`}
      >
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{renderMessageContent(message.content)}</p>
      </div>
    </motion.div>
  );
};

export default function ChatPage() {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showContactButton, setShowContactButton] = useState(false);
  const [showInfoCheckButtons, setShowInfoCheckButtons] = useState(false); // For two-button info check
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [modalWithAppointment, setModalWithAppointment] = useState(false); // true = Termin-Formular, false = nur Kontakt
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [appointmentData, setAppointmentData] = useState({ date: null, time: null });
  const [chatClosed, setChatClosed] = useState(false);
  const [wantsAppointment, setWantsAppointment] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]); // Available appointment slots
  const [selectedSlot, setSelectedSlot] = useState(null); // Selected slot for confirmation
  const [chatMode, setChatMode] = useState(''); // text, voice, phone, choice - empty until loaded
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showModeChoice, setShowModeChoice] = useState(false); // Show mode choice buttons
  const [modeChoiceMessage, setModeChoiceMessage] = useState(''); // Custom message for mode choice
  const [userHasChosenMode, setUserHasChosenMode] = useState(false); // User has made their choice
  const [isInitializing, setIsInitializing] = useState(true); // Show loading while initializing
  const [isSlotModalOpen, setIsSlotModalOpen] = useState(false); // Slot selection modal
  const [appointmentMode, setAppointmentMode] = useState('with_appointment'); // 'with_appointment' or 'contact_only'
  
  // Voice Recording States (before contact form)
  const [voiceSettings, setVoiceSettings] = useState({ enabled: false, allow_user_choice: false, max_duration_seconds: 30, prompt_text: "" });
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [voiceCompleted, setVoiceCompleted] = useState(false);
  const [showVoiceChoiceButtons, setShowVoiceChoiceButtons] = useState(false);
  
  // NEW: Onboarding Flow States
  // 0=motivation, 1=follow-up, 2=18+, 3=working_status, 4=start_date, 6=job_preference, 7=done (age_group step 5 removed)
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [onboardingData, setOnboardingData] = useState({
    motivation_choice: null,
    is_18_plus: null,
    current_work_status: null,
    available_from: null,
    age_group: null,
    job_preference: null
  });
  const [showOnboardingButtons, setShowOnboardingButtons] = useState(true);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const initializedRef = useRef(false);

  // Improved scroll - show user's message + start of bot response
  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        const scrollContainer = document.querySelector('[data-testid="chat-scroll-area"]');
        if (!scrollContainer) return;
        
        // Strategy: Find the last USER message and scroll to show it + the bot response below
        const userMessages = scrollContainer.querySelectorAll('[data-testid^="chat-message-user"]');
        const lastUserMessage = userMessages[userMessages.length - 1];
        
        if (lastUserMessage) {
          // Scroll to show the user's message at the top, so bot response is visible below
          const containerRect = scrollContainer.getBoundingClientRect();
          const messageRect = lastUserMessage.getBoundingClientRect();
          
          // Position user message near top with padding
          const scrollTop = scrollContainer.scrollTop + (messageRect.top - containerRect.top) - 30;
          
          scrollContainer.scrollTo({
            top: Math.max(0, scrollTop),
            behavior: 'smooth'
          });
        } else {
          // No user messages yet - just scroll to top to show bot greeting
          scrollContainer.scrollTop = 0;
        }
      }, 150);
    });
  };

  // Check for ?new=1 URL parameter to force new chat
  const [isNewChat, setIsNewChat] = useState(false);
  
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('new') === '1') {
      // Clear session and start fresh
      clearChatSession();
      // Reset mode choice state
      setUserHasChosenMode(false);
      initializedRef.current = false;
      localStorage.removeItem('hype_chat_user_mode');
      setMessages([]);
      setSessionId(null);
      setIsNewChat(true);
      // Remove the parameter from URL without reload
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Fetch chat mode setting on mount with retry
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 3;
    
    const fetchChatMode = async () => {
      try {
        const response = await axios.get(`${API}/chat-mode`);
        const savedMode = localStorage.getItem('hype_chat_user_mode');
        
        // IMPORTANT: Set appointment_mode from backend
        if (response.data.appointment_mode) {
          setAppointmentMode(response.data.appointment_mode);
        }
        
        // Check if mode choice is enabled and user hasn't chosen yet (or new chat)
        if (response.data.show_mode_choice && (!savedMode || isNewChat)) {
          setShowModeChoice(true);
          setModeChoiceMessage(response.data.mode_choice_message || '');
          setChatMode('choice'); // Special mode for showing choice
        } else if (savedMode && !isNewChat) {
          // User already chose their mode
          setChatMode(savedMode);
          setUserHasChosenMode(true);
        } else {
          setChatMode(response.data.active_mode || 'text');
        }
        
        if (response.data.phone_number) {
          setPhoneNumber(response.data.phone_number);
        }
      } catch (error) {
        console.log('Error fetching chat mode, attempt:', retryCount + 1);
        retryCount++;
        if (retryCount < maxRetries) {
          // Retry after delay
          setTimeout(fetchChatMode, 500 * retryCount);
        } else {
          console.log('Using default text mode after retries');
          setChatMode('text');
        }
      }
    };
    
    // Small delay to ensure component is fully mounted
    const timeoutId = setTimeout(fetchChatMode, 100);
    return () => clearTimeout(timeoutId);
  }, [isNewChat]);

  // Initialize session and send welcome message - wait for mode data
  useEffect(() => {
    // Don't initialize until we know the chat mode settings
    if (chatMode === '') return;
    
    // Don't initialize twice
    if (initializedRef.current) return;
    
    // If this is a new chat, force initialization
    if (isNewChat) {
      initializedRef.current = true;
      initializeChat();
      return;
    }
    
    // Don't re-initialize if user already has a session
    const existingSessionId = localStorage.getItem('hype_chat_session_id');
    const existingMessages = localStorage.getItem('hype_chat_messages');
    const existingShowButton = localStorage.getItem('hype_chat_show_button');
    const existingAppointment = localStorage.getItem('hype_chat_appointment');
    const savedUserMode = localStorage.getItem('hype_chat_user_mode');
    const savedAppointmentMode = localStorage.getItem('hype_chat_appointment_mode');
    
    // If user has already chosen a mode and has existing session
    if (savedUserMode && existingSessionId && existingMessages) {
      try {
        const parsedMessages = JSON.parse(existingMessages);
        if (parsedMessages && parsedMessages.length > 0) {
          setUserHasChosenMode(true);
          setChatMode(savedUserMode);
          setShowModeChoice(false);
          setSessionId(existingSessionId);
          setMessages(parsedMessages);
          setIsInitializing(false); // Important: stop loading state
          initializedRef.current = true;
          
          if (existingShowButton === 'true') {
            setShowContactButton(true);
          }
          if (existingAppointment) {
            setAppointmentData(JSON.parse(existingAppointment));
          }
          // Restore appointment mode
          if (savedAppointmentMode) {
            setAppointmentMode(savedAppointmentMode);
          }
          return; // Don't initialize again
        }
      } catch (e) {
        // If parsing fails, continue to initialize
        console.log('Failed to parse existing messages, reinitializing');
      }
    }
    
    // Initialize new session
    initializedRef.current = true;
    initializeChat();
  }, [chatMode, showModeChoice, modeChoiceMessage, isNewChat]);

  // Save session state to localStorage whenever it changes
  useEffect(() => {
    if (sessionId) {
      localStorage.setItem('hype_chat_session_id', sessionId);
    }
  }, [sessionId]);

  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('hype_chat_messages', JSON.stringify(messages));
    }
  }, [messages]);

  useEffect(() => {
    localStorage.setItem('hype_chat_show_button', showContactButton.toString());
  }, [showContactButton]);

  useEffect(() => {
    if (appointmentData.date || appointmentData.time) {
      localStorage.setItem('hype_chat_appointment', JSON.stringify(appointmentData));
    }
  }, [appointmentData]);

  // Save appointment mode to localStorage
  useEffect(() => {
    localStorage.setItem('hype_chat_appointment_mode', appointmentMode);
  }, [appointmentMode]);

  // Auto-scroll when messages change
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  // Sync messages to backend periodically and on page leave
  useEffect(() => {
    const syncMessages = async () => {
      if (sessionId && messages.length > 0) {
        try {
          await axios.post(`${API}/chat/sync-messages`, {
            session_id: sessionId,
            messages: messages.map(m => ({
              role: m.role,
              content: m.content,
              timestamp: m.timestamp || new Date().toISOString()
            }))
          });
          console.log("Messages synced:", messages.length);
        } catch (error) {
          console.error("Error syncing messages:", error);
        }
      }
    };

    // Sync messages every 30 seconds
    const syncInterval = setInterval(syncMessages, 30000);

    // Sync on page leave - only sync messages, do NOT create partial leads
    const handleBeforeUnload = () => {
      if (sessionId && messages.length > 0) {
        const data = JSON.stringify({
          session_id: sessionId,
          messages: messages.map(m => ({
            role: m.role,
            content: m.content,
            timestamp: m.timestamp || new Date().toISOString()
          }))
        });
        navigator.sendBeacon(`${API}/chat/sync-messages`, new Blob([data], { type: 'application/json' }));
      }
      // NO partial lead creation - leads only from contact form!
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      clearInterval(syncInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Final sync when component unmounts
      syncMessages();
    };
  }, [sessionId, messages]);

  const initializeChat = async () => {
    setIsInitializing(true);
    
    // Reset voice state for new chat - IMPORTANT!
    setVoiceCompleted(false);
    setShowVoiceRecorder(false);
    
    let retryCount = 0;
    const maxRetries = 5; // Increased retries
    
    const tryInitialize = async () => {
      try {
        // Create new session with timeout
        const sessionResponse = await axios.post(`${API}/chat/session`, {}, {
          timeout: 15000 // 15 second timeout (increased)
        });
        const newSessionId = sessionResponse.data.session_id;
        setSessionId(newSessionId);
        
        // Fetch voice settings for HypeFactory
        try {
          const voiceResponse = await axios.get(`${API}/voice-settings/hypefactory`);
          setVoiceSettings(voiceResponse.data);
        } catch (err) {
          console.log("Voice settings not available");
        }
        
        // Check if we should show mode choice or normal welcome
        if (showModeChoice && modeChoiceMessage && !userHasChosenMode) {
          // Show mode choice message
          setMessages([{
            role: "assistant",
            content: modeChoiceMessage,
            showModeChoice: true
          }]);
          setShowOnboardingButtons(false);
        } else {
          // NEW: Start with onboarding flow - Step 0 (Start/Commitment)
          setOnboardingStep(0);
          setShowOnboardingButtons(true);
          setMessages([{
            role: "assistant",
            content: "Hey 👋\nIch bin Lea 😊\n\nSchön, dass du hier bist.\n\nBevor ich dir alles erkläre – **was im Bereich Promotion alles möglich ist.**\nwas interessiert dich gerade am meisten?\n\n👉 Was genau macht man & wie läuft das ab?\n👉 Was kann man verdienen?\n👉 Am liebsten alles 😊\n\nKlick einfach das an, was dich am meisten interessiert 👇\n\nOder schreib mir einfach direkt hier im Chat –\nich antworte dir gerne 😊\n\n📞 **Keine Lust zu tippen? Ruf mich direkt an:**\n**0211 86943288**"
          }]);
        }
        return true; // Success
      } catch (error) {
        console.error(`Chat initialization attempt ${retryCount + 1} failed:`, error);
        return false; // Failed
      }
    };
    
    // Retry loop with longer waits
    while (retryCount < maxRetries) {
      const success = await tryInitialize();
      if (success) {
        setIsInitializing(false);
        return;
      }
      retryCount++;
      if (retryCount < maxRetries) {
        // Wait before retry (exponential backoff, max 5 seconds)
        await new Promise(resolve => setTimeout(resolve, Math.min(1500 * retryCount, 5000)));
      }
    }
    
    // All retries failed - show friendly message without technical jargon
    setMessages([{
      role: "assistant",
      content: "Hey 👋 ich bin Lea! Einen Moment bitte, ich lade gerade... 🔄\n\nKlicke bitte auf 'Neuer Chat' oder lade die Seite neu (F5)."
    }]);
    setIsInitializing(false);
  };

  // Helper to save message to backend
  const saveMessageToBackend = async (message) => {
    if (!sessionId) return;
    try {
      await axios.post(`${API}/chat/save-message`, {
        session_id: sessionId,
        project_id: "hypefactory",
        message: message
      });
    } catch (err) {
      console.error("Error saving message:", err);
    }
  };

  // Function to show contact form or voice recorder first
  const proceedToContactForm = () => {
    // If voice is enabled and not yet completed
    if (voiceSettings.enabled && !voiceCompleted) {
      // Check if user should choose
      if (voiceSettings.allow_user_choice) {
        // Show choice buttons instead of voice recorder directly
        setShowVoiceChoiceButtons(true);
        setShowContactButton(false);
        setShowVoiceRecorder(false);
      } else {
        // Show voice recorder directly (no choice)
        setShowVoiceRecorder(true);
        setShowContactButton(false);
        setShowVoiceChoiceButtons(false);
      }
    } else {
      // Voice not enabled or already completed - show contact form directly
      setShowContactButton(true);
      setShowVoiceRecorder(false);
      setShowVoiceChoiceButtons(false);
    }
  };

  // Handle user choice: Voice or Contact form
  const handleVoiceChoice = (choice) => {
    setShowVoiceChoiceButtons(false);
    if (choice === 'voice') {
      // User wants to record voice
      setShowVoiceRecorder(true);
      setShowContactButton(false);
    } else {
      // User wants to skip voice - open contact form DIRECTLY
      setVoiceCompleted(true);
      setShowVoiceRecorder(false);
      setShowContactButton(false);
      // Open contact modal immediately
      setIsContactModalOpen(true);
    }
  };

  // Handle voice recording completion
  const handleVoiceComplete = async (voiceData) => {
    setVoiceCompleted(true);
    setShowVoiceRecorder(false);
    
    // Add user message showing they recorded
    const userVoiceMessage = {
      role: "user",
      content: "🎤 Sprachnachricht aufgenommen"
    };
    setMessages(prev => [...prev, userVoiceMessage]);
    await saveMessageToBackend(userVoiceMessage);
    
    // Add confirmation message WITHOUT transcript (transcript only visible in admin)
    const confirmMessage = {
      role: "assistant", 
      content: `Super, danke dir für deine Sprachnachricht! 🎤\n\nJetzt bitte noch **unbedingt das Kontaktformular ausfüllen** – erst dann ist deine Bewerbung vollständig im System.\n\nAuch wenn du schon mal in einem anderen Chat Daten eingetragen hast, ist dieser Schritt hier jetzt trotzdem noch nötig.`
    };
    setMessages(prev => [...prev, confirmMessage]);
    await saveMessageToBackend(confirmMessage);
    
    // Now show contact button
    setShowContactButton(true);
  };

  // NEW: Handle onboarding button clicks
  const handleOnboardingButton = async (value) => {
    // Add user's choice as a message
    setMessages(prev => [...prev, { role: "user", content: value }]);
    setShowOnboardingButtons(false);
    setIsLoading(true);

    // Small delay to show the message
    await new Promise(resolve => setTimeout(resolve, 500));

    if (onboardingStep === 0) {
      // Step 0: Motivation question -> Go to Step 1 (Follow-up based on choice)
      setOnboardingStep(1);
      
      // Different follow-up based on user's choice
      let followUpMessage = "";
      if (value === "Was genau macht man & wie läuft das ab?") {
        followUpMessage = "Super 😊\n\nKurz erklärt:\n\nDu bist im Team unterwegs\nbei Events, Promotionaktionen oder Projekten.\n\nMeist arbeitet man mit 2–5 Leuten zusammen,\nes ist abwechslungsreich und man ist nicht nur an einem Ort.\n\nBevor ich weiter ins Detail gehe:\n\nArbeitest du aktuell oder suchst du gerade aktiv etwas Neues?";
      } else if (value === "Was kann man verdienen?") {
        followUpMessage = "Gute Frage 😊\n\nBei Vollzeit liegt das Einstiegsgehalt\nzwischen **2.600 € und 3.200 € pro Monat** – ab dem ersten Tag, auch während der 14-Tage Probearbeiten-Phase.\n\nUnterkunft, Verpflegung und Anreise sind bei Reise-Jobs komplett kostenlos on top.\n\nWichtiger für mich:\n\nSuchst du aktuell etwas in Vollzeit?";
      } else {
        // "Am liebsten alles"
        followUpMessage = "Sehr gut 😄\n\nKurz zusammengefasst:\n\nEs geht um einen Vollzeit-Job im Event- & Promotionsbereich,\nman ist viel im Team unterwegs\nund das Einstiegsgehalt liegt zwischen **2.600 € und 3.200 € pro Monat** – ab dem ersten Tag.\n\nUnterkunft, Verpflegung und Anreise komplett kostenlos.\n\nJetzt die wichtigste Frage:\n\nSuchst du aktuell wirklich eine neue Vollzeitstelle?";
      }
      
      // Store the choice to determine buttons
      setOnboardingData(prev => ({ ...prev, motivation_choice: value }));
      
      setMessages(prev => [...prev, {
        role: "assistant",
        content: followUpMessage
      }]);
      setShowOnboardingButtons(true);
    }
    else if (onboardingStep === 1) {
      // Step 1: Follow-up answer -> Go to 18+ Gate
      // All paths lead to 18+ gate regardless of answer
      setOnboardingStep(2);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Perfekt 👍\n\nGanz kurz vorab – nur zur Sicherheit 😊\n\nBist du mindestens 18 Jahre alt?"
      }]);
      setShowOnboardingButtons(true);
    } 
    else if (onboardingStep === 2) {
      // Step 2: 18+ Gate
      const is18Plus = value === "Ja ✓";
      setOnboardingData(prev => ({ ...prev, is_18_plus: is18Plus }));
      
      if (!is18Plus) {
        // Under 18 - end chat
        setMessages(prev => [...prev, {
          role: "assistant",
          content: "Schade! 😊 Leider musst du mindestens 18 Jahre alt sein, um bei uns zu starten.\n\nMelde dich gerne wieder, wenn du volljährig bist! 🎉"
        }]);
        setChatClosed(true);
        setShowOnboardingButtons(false);
        // Save to session that user is under 18
        try {
          await axios.post(`${API}/chat/onboarding-data`, {
            session_id: sessionId,
            is_18_plus: false
          });
        } catch (e) { console.error(e); }
      } else {
        // 18+ - continue to Step 3 (Arbeitsstatus)
        setOnboardingStep(3);
        setMessages(prev => [...prev, {
          role: "assistant",
          content: "Super 😊\n\nArbeitest du aktuell?"
        }]);
        setShowOnboardingButtons(true);
      }
    }
    else if (onboardingStep === 3) {
      // Step 3: Working status - now with detailed options
      setOnboardingData(prev => ({ ...prev, current_work_status: value }));
      
      // If "Auf Jobsuche" -> skip start date question, go straight to job preference (no age group)
      if (value === "Auf Jobsuche") {
        setOnboardingStep(6);
        setMessages(prev => [...prev, {
          role: "assistant",
          content: "Hey 👋\nGanz kurz vorab:\n\nHast du eher Lust auf\n\n👉 Events / unterwegs sein\n👉 Geld verdienen\n👉 Beides"
        }]);
      } else {
        // For Angestellt/Selbstständig/Student -> ask when they can start
        setOnboardingStep(4);
        setMessages(prev => [...prev, {
          role: "assistant",
          content: "Alles klar 👍\n\nUnd wann könntest du bei uns anfangen?"
        }]);
      }
      setShowOnboardingButtons(true);
    }
    else if (onboardingStep === 4) {
      // Step 4: Start date (only for employed/self-employed/student) -> Skip age group, go to job preference
      setOnboardingData(prev => ({ ...prev, available_from: value }));
      
      setOnboardingStep(6);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Hey 👋\nGanz kurz vorab:\n\nHast du eher Lust auf\n\n👉 Events / unterwegs sein\n👉 Geld verdienen\n👉 Beides"
      }]);
      setShowOnboardingButtons(true);
    }
    else if (onboardingStep === 6) {
      // Step 6: Motivation/Preference
      setOnboardingData(prev => ({ ...prev, job_preference: value }));
      
      // Save all onboarding data to backend
      const finalWorkStatus = onboardingData.current_work_status;
      const finalAvailableFrom = onboardingData.available_from;
      const finalAgeGroup = onboardingData.age_group;
      
      try {
        const finalData = {
          session_id: sessionId,
          is_18_plus: true,
          current_work_status: finalWorkStatus,
          available_from: finalAvailableFrom,
          age_group: finalAgeGroup,
          job_preference: value
        };
        await axios.post(`${API}/chat/onboarding-data`, finalData);
      } catch (e) { console.error(e); }
      
      // Onboarding complete - continue to normal chat
      setOnboardingStep(7);
      setShowOnboardingButtons(false);
      
      // Build intro message based on work status
      let statusText = "";
      if (finalWorkStatus === "Auf Jobsuche") {
        statusText = "bin gerade auf Jobsuche";
      } else if (finalWorkStatus === "Student/Schüler") {
        statusText = `bin ${finalWorkStatus} und könnte ${finalAvailableFrom || "flexibel"} anfangen`;
      } else {
        statusText = `bin aktuell ${finalWorkStatus?.toLowerCase() || ""} und könnte ${finalAvailableFrom || "flexibel"} anfangen`;
      }
      
      const introMessage = `Ich bin volljährig und ${statusText}. Mich interessiert: ${value}.`;
      
      try {
        const response = await axios.post(`${API}/chat/message`, {
          session_id: sessionId,
          message: introMessage
        });
        
        setMessages(prev => [...prev, {
          role: "assistant",
          content: response.data.response
        }]);
        
        // Handle response flags
        // WORKAROUND: If response contains a question, don't show contact button yet
        const responseText = response.data.response || "";
        const hasQuestion = responseText.includes("?");
        
        if (response.data.show_contact_button && !hasQuestion) {
          proceedToContactForm();
        }
        if (response.data.appointment_mode) {
          setAppointmentMode(response.data.appointment_mode);
        }
      } catch (error) {
        console.error('Error sending intro message:', error);
        setMessages(prev => [...prev, {
          role: "assistant",
          content: "Super, danke! 😊 Erzähl mir mehr über dich – bist du bereit, deutschlandweit zu reisen für spannende Projekte?"
        }]);
      }
    }
    
    setIsLoading(false);
  };

  // Get current onboarding buttons based on step
  const getOnboardingButtons = () => {
    switch (onboardingStep) {
      case 0: // Start - motivation question
        return [
          { label: "🔍 Was macht man & wie läuft's ab?", value: "Was genau macht man & wie läuft das ab?", color: "green" },
          { label: "💰 Was kann man verdienen?", value: "Was kann man verdienen?", color: "green" },
          { label: "😊 Am liebsten alles", value: "Am liebsten alles", color: "green" }
        ];
      case 1: // Follow-up based on motivation choice
        if (onboardingData.motivation_choice === "Was genau macht man & wie läuft das ab?") {
          return [
            { label: "👔 Arbeite aktuell", value: "Arbeite aktuell", color: "green" },
            { label: "🔍 Suche aktiv", value: "Suche aktiv", color: "green" },
            { label: "👀 Nur mal schauen", value: "Nur mal schauen", color: "green" }
          ];
        } else if (onboardingData.motivation_choice === "Was kann man verdienen?") {
          return [
            { label: "✅ Ja", value: "Ja", color: "green" },
            { label: "🤔 Eventuell", value: "Eventuell", color: "green" },
            { label: "❓ Noch unsicher", value: "Noch unsicher", color: "green" }
          ];
        } else {
          // "Am liebsten alles"
          return [
            { label: "✅ Ja", value: "Ja", color: "green" },
            { label: "🤔 Vielleicht", value: "Vielleicht", color: "green" },
            { label: "ℹ️ Nur Infos", value: "Nur Infos", color: "green" }
          ];
        }
      case 2: // 18+ Gate
        return [
          { label: "Ja ✓", value: "Ja ✓", color: "green" },
          { label: "Nein", value: "Nein", color: "red" }
        ];
      case 3: // Working status - detailed options
        return [
          { label: "Angestellt", value: "Angestellt", color: "green" },
          { label: "Selbstständig", value: "Selbstständig", color: "green" },
          { label: "Student/Schüler", value: "Student/Schüler", color: "green" },
          { label: "Auf Jobsuche", value: "Auf Jobsuche", color: "green" }
        ];
      case 4: // Start date (when can you start)
        return [
          { label: "Sofort", value: "Sofort", color: "green" },
          { label: "In 2 Wochen", value: "In 2 Wochen", color: "green" },
          { label: "In 1 Monat", value: "In 1 Monat", color: "green" },
          { label: "Später", value: "Später", color: "green" }
        ];
      case 6: // Job preference (Events/Geld/Beides)
        return [
          { label: "🎉 Events / unterwegs sein", value: "Events / unterwegs sein", color: "green" },
          { label: "💰 Geld verdienen", value: "Geld verdienen", color: "green" },
          { label: "🔥 Beides", value: "Beides", color: "green" }
        ];
      default:
        return [];
    }
  };

  // Handle user mode selection
  const handleModeSelect = async (selectedMode) => {
    console.log('Mode selected:', selectedMode, 'sessionId:', sessionId);
    
    // Save user's choice
    localStorage.setItem('hype_chat_user_mode', selectedMode);
    setChatMode(selectedMode);
    setUserHasChosenMode(true);
    setShowModeChoice(false);
    
    // Add user's choice as a message
    const choiceText = selectedMode === 'text' ? '✍️ Ich möchte schreiben' : '🎤 Ich möchte sprechen';
    
    if (selectedMode === 'voice') {
      // Voice mode - use old flow
      const followUpMessage = "Super, dann sprich einfach drauf los! 🎤\n\nAls Erstes: Wie alt bist du?";
      setMessages(prev => {
        const updatedMessages = prev.map(msg => ({...msg, showModeChoice: false}));
        return [
          ...updatedMessages,
          { role: "user", content: choiceText },
          { role: "assistant", content: followUpMessage }
        ];
      });
      setShowOnboardingButtons(false);
    } else {
      // Text mode - start NEW onboarding flow
      setOnboardingStep(0);
      setShowOnboardingButtons(true);
      setMessages(prev => {
        const updatedMessages = prev.map(msg => ({...msg, showModeChoice: false}));
        return [
          ...updatedMessages,
          { role: "user", content: choiceText },
          { role: "assistant", content: "Hey 👋\nIch bin Lea 😊\n\nSchön, dass du hier bist.\n\nBevor ich dir alles erkläre – **was im Bereich Promotion alles möglich ist.**\nwas interessiert dich gerade am meisten?\n\n👉 Was genau macht man & wie läuft das ab?\n👉 Was kann man verdienen?\n👉 Am liebsten alles 😊\n\nKlick einfach das an, was dich am meisten interessiert 👇\n\n📞 **Keine Lust zu tippen? Ruf mich direkt an:**\n**0211 86943288**" }
        ];
      });
    }
    
    // Scroll to bottom
    setTimeout(scrollToBottom, 100);
  };

  const sendMessage = async () => {
    // Validate - but don't block silently
    const messageText = inputValue.trim();
    if (!messageText) {
      console.log('No message to send');
      return;
    }
    if (isLoading) {
      console.log('Already loading, please wait');
      return;
    }
    if (!sessionId) {
      console.log('No session ID');
      return;
    }
    if (chatClosed) {
      console.log('Chat is closed');
      return;
    }

    // If user types a message during onboarding Step 0, skip the welcome buttons
    // but continue with the normal onboarding flow (18+ gate etc.)
    if (onboardingStep === 0) {
      setOnboardingStep(2); // Go to 18+ Gate
      // Buttons stay visible for next steps
    }

    // Stop any playing audio when user sends a new message
    stopCurrentAudio();

    // Clear input immediately
    setInputValue("");
    
    // Add user message to chat
    setMessages(prev => [...prev, { role: "user", content: messageText }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: messageText
      }, {
        timeout: 30000 // 30 second timeout for chat messages
      });
      
      // Add bot response
      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
      
      // Check for available slots (for clickable selection)
      if (response.data.available_slots && response.data.available_slots.length > 0) {
        setAvailableSlots(response.data.available_slots);
        setSelectedSlot(null); // Reset selection
      }
      
      // Check for appointment data from chat
      if (response.data.appointment_date || response.data.appointment_time) {
        setAppointmentData({
          date: response.data.appointment_date || appointmentData.date,
          time: response.data.appointment_time || appointmentData.time
        });
        // Clear slots after appointment is set
        setAvailableSlots([]);
        setSelectedSlot(null);
      }
      
      // WICHTIG: States müssen IMMER aktualisiert werden (nicht nur wenn true)
      // Sonst bleiben alte States hängen!
      setShowInfoCheckButtons(response.data.show_info_check_buttons === true);
      
      // Update appointment mode from backend
      if (response.data.appointment_mode) {
        setAppointmentMode(response.data.appointment_mode);
      }
      
      // Handle show_contact_button with Voice-Check
      // WORKAROUND: If response contains a question, don't show contact button yet
      const responseText = response.data.response || "";
      const hasQuestion = responseText.includes("?");
      
      if (response.data.show_contact_button === true && !hasQuestion) {
        // Check if voice recording should happen first
        if (voiceSettings.enabled && !voiceCompleted) {
          // Voice recording needed first
          proceedToContactForm();
        } else {
          // No voice needed - show button directly
          setShowContactButton(true);
        }
      } else {
        setShowContactButton(false);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "Entschuldigung, es gab ein technisches Problem. Bitte versuche es erneut." 
      }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  // Handle info check button clicks
  const handleInfoCheckTermin = async () => {
    // User wants to make appointment directly
    setShowInfoCheckButtons(false);
    setIsLoading(true);
    
    try {
      // Add user message
      setMessages(prev => [...prev, { role: "user", content: "Termin festmachen" }]);
      
      // Send to backend
      const response = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: "Ja, Termin festmachen"
      });
      
      // Add bot response
      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
      
      // Set appointment data if available
      if (response.data.appointment_date) {
        setAppointmentData({
          date: response.data.appointment_date,
          time: response.data.appointment_time || "13:00"
        });
        setWantsAppointment(true);
      }
      
      // Show contact button with appointment
      if (response.data.show_contact_button) {
        proceedToContactForm();
      }
      
      scrollToBottom();
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInfoCheckKontakt = () => {
    // User wants contact form WITHOUT appointment
    setAppointmentMode('contact_only');
    setShowInfoCheckButtons(false); // Hide info check buttons
    
    // Check if voice recording should happen first
    if (voiceSettings.enabled && !voiceCompleted) {
      // Show voice recorder first, then contact form
      proceedToContactForm();
    } else {
      // No voice needed - open contact form directly
      setModalWithAppointment(false);
      setIsContactModalOpen(true);
    }
  };

  // Open modal with appointment (for Termin button)
  const openModalWithTermin = () => {
    setModalWithAppointment(true);
    setIsContactModalOpen(true);
  };

  // Handle slot confirmation
  const handleSlotConfirm = async () => {
    if (!selectedSlot) return;
    
    setIsLoading(true);
    
    try {
      // Book the slot
      await axios.post(`${API}/book-slot/${selectedSlot.id}`, { session_id: sessionId });
      
      // Send message to bot with the selected slot number
      const response = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: selectedSlot.number.toString()
      });
      
      // Add confirmation to chat
      setMessages(prev => [...prev, 
        { role: "user", content: `Termin: ${selectedSlot.display}` },
        { role: "assistant", content: response.data.response }
      ]);
      
      // Set appointment data - date is in DD.MM.YYYY format from API
      setAppointmentData({
        date: selectedSlot.date,  // DD.MM.YYYY format (e.g., "03.02.2026")
        time: selectedSlot.time
      });
      
      // Clear slots and show contact button
      setAvailableSlots([]);
      setSelectedSlot(null);
      proceedToContactForm();
      
    } catch (error) {
      console.error("Error confirming slot:", error);
    } finally {
      setIsLoading(false);
      setIsSlotModalOpen(false);  // Close modal
    }
  };

  // Clear session from localStorage (called after form submission or for fresh start)
  const clearChatSession = () => {
    localStorage.removeItem('hype_chat_session_id');
    localStorage.removeItem('hype_chat_messages');
    localStorage.removeItem('hype_chat_show_button');
    localStorage.removeItem('hype_chat_appointment');
    // Reset voice state
    setVoiceCompleted(false);
    setShowVoiceRecorder(false);
  };

  // Handle closing the chat after submission
  const handleCloseChat = () => {
    setChatClosed(true);
    clearChatSession();
  };

  // Handle starting appointment booking
  const handleStartAppointment = async () => {
    setWantsAppointment(true);
    // Send message to bot that user wants appointment
    const userMessage = "Ja, ich möchte einen Termin vereinbaren!";
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: userMessage
      });
      
      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
      
      if (response.data.appointment_date || response.data.appointment_time) {
        setAppointmentData({
          date: response.data.appointment_date || appointmentData.date,
          time: response.data.appointment_time || appointmentData.time
        });
      }
      
      if (response.data.show_contact_button) {
        proceedToContactForm();
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle direct contact (no appointment)
  const handleDirectContact = () => {
    setWantsAppointment(false);
    setIsContactModalOpen(true);
  };

  // Handle voice chat response
  const handleVoiceResponse = (response) => {
    // Add user message
    setMessages(prev => [...prev, { role: "user", content: response.userText }]);
    // Add bot response
    setMessages(prev => [...prev, { role: "assistant", content: response.botText }]);
    
    // Scroll to bottom after response
    setTimeout(scrollToBottom, 100);
    
    // Handle contact button and slots
    if (response.showContactButton) {
      proceedToContactForm();
    }
    if (response.availableSlots && response.availableSlots.length > 0) {
      setAvailableSlots(response.availableSlots);
      setSelectedSlot(null);
    }
  };

  // Handle voice transcription (show user what they said)
  const handleVoiceTranscription = (text) => {
    // Transcription is handled in handleVoiceResponse
    console.log('Transcribed:', text);
  };

  const handleContactSuccess = (type) => {
    setIsContactModalOpen(false);
    setIsSubmitted(true);
    
    // DON'T clear session - allow continued questions
    // clearChatSession();
    
    if (type === 'appointment' && appointmentData.date && appointmentData.time) {
      // Mit Termin - Bestätigungs-Nachricht
      setMessages(prev => [...prev, {
        role: "assistant",
        content: `Super, alles erledigt! 🎉

**Dein Termin ist bestätigt:**
📅 ${appointmentData.date}
🕐 ${appointmentData.time}
📍 HYPEFactory, Grafenberger Allee 293, 40237 Düsseldorf (8. Etage)

Du bekommst jetzt eine E-Mail mit:
• Termin-Bestätigung
• Adresse & Anfahrt (Straßenbahn 709)
• Checkliste was du mitbringen sollst

**Wichtig:** Bitte ruf einen Arbeitstag vor deinem Termin zwischen 12:00 und 13:00 Uhr unter **01756001719** an, um den Termin zu bestätigen. Dann bekommst du auch dein DB-Ticket!

Wir freuen uns auf dich! 🙌

💬 **Hast du noch Fragen? Schreib einfach!**`
      }]);
    } else {
      // Ohne Termin - Lea ruft an
      setMessages(prev => [...prev, {
        role: "assistant",
        content: `Danke dir! 👍

Wir haben deine Daten erhalten.
**Lea meldet sich schnellstmöglich telefonisch bei dir**, um alles Weitere zu besprechen.

💬 **Hast du noch Fragen? Schreib einfach!**`
      }]);
    }
  };

  return (
    <div className="h-[100dvh] bg-gradient-to-br from-slate-50 via-white to-blue-50 flex flex-col relative noise-overlay overflow-hidden">
      {/* Header */}
      <header className="glass border-b border-slate-200/50 sticky top-0 z-10 flex-shrink-0">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Hype-Factory
              </h1>
              <p className="text-xs text-slate-500">Bewerber-Chat</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-2 text-xs text-slate-500">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
              Online
            </span>
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  stopCurrentAudio();
                  clearChatSession();
                  localStorage.removeItem('hype_chat_user_mode');
                  window.location.href = window.location.pathname + '?new=1';
                }}
                className="text-xs text-slate-500 hover:text-slate-700"
                data-testid="new-chat-button"
              >
                Neuer Chat
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Chat Container */}
      <main className="flex-1 flex flex-col max-w-2xl mx-auto w-full overflow-hidden">
        <div className="flex-1 overflow-y-auto px-4 mobile-scroll chat-scroll-container" data-testid="chat-scroll-area">
          <div className="py-6 space-y-4 pb-4">
            {/* Loading indicator while initializing */}
            {isInitializing && messages.length === 0 && (
              <div className="flex justify-center py-8">
                <div className="flex items-center gap-3 text-slate-500">
                  <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span>Chat wird geladen...</span>
                </div>
              </div>
            )}
            
            <AnimatePresence mode="popLayout">
              {messages.map((msg, idx) => (
                <ChatMessage key={idx} message={msg} index={idx} />
              ))}
            </AnimatePresence>
            
            {/* Mode Choice Buttons */}
            {messages.length > 0 && messages[messages.length - 1]?.showModeChoice && !userHasChosenMode && (
              <ModeChoiceButtons
                onSelectText={() => handleModeSelect('text')}
                onSelectVoice={() => handleModeSelect('voice')}
              />
            )}
            
            {/* NEW: Onboarding Buttons */}
            {showOnboardingButtons && onboardingStep < 7 && !chatClosed && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-wrap gap-2 justify-center mt-4 mb-2"
                data-testid="onboarding-buttons"
              >
                {getOnboardingButtons().map((btn, idx) => (
                  <motion.button
                    key={idx}
                    onClick={() => handleOnboardingButton(btn.value)}
                    disabled={isLoading}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`px-6 py-3 rounded-full font-semibold text-white shadow-lg transition-all ${
                      btn.color === 'green' 
                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-green-500/30' 
                        : 'bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 shadow-red-500/30'
                    } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid={`onboarding-btn-${idx}`}
                  >
                    {btn.label}
                  </motion.button>
                ))}
              </motion.div>
            )}
            
            {isLoading && <TypingIndicator />}
            
            {/* ALTE SLOT-AUSWAHL DEAKTIVIERT - Fester Termin (Montag 13:00 Folgewoche) wird jetzt automatisch gesetzt
            {availableSlots.length > 0 && !isSubmitted && (
              <SlotSelectionButton
                onClick={() => setIsSlotModalOpen(true)}
                slotsCount={availableSlots.length}
              />
            )}
            */}
            
            {/* Scroll anchor */}
            <div ref={messagesEndRef} />
          </div>
        </div>
        
        {/* ACTION BUTTONS - FIXED POSITION ABOVE INPUT (Mobile-friendly) */}
        {/* Info Check Buttons - erscheinen direkt nach Info-Check */}
        {showInfoCheckButtons && !isSubmitted && !appointmentData.date && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex-shrink-0 bg-gradient-to-t from-white via-white to-white/95 border-t border-slate-100 px-4 py-3"
            data-testid="info-check-buttons-container"
          >
            <div className="flex flex-col gap-2 max-w-md mx-auto">
              <Button
                onClick={handleInfoCheckTermin}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl py-4 text-base font-semibold shadow-lg shadow-green-500/30"
                data-testid="info-check-termin-button"
              >
                📅 TERMIN FESTMACHEN
              </Button>
              <Button
                onClick={handleInfoCheckKontakt}
                className="w-full bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white rounded-xl py-4 text-base font-semibold shadow-lg shadow-blue-500/30"
                data-testid="info-check-kontakt-button"
              >
                📞 KONTAKT AUFNEHMEN
              </Button>
            </div>
          </motion.div>
        )}
        
        {/* Voice Choice Buttons - User can choose Voice or Contact */}
        {showVoiceChoiceButtons && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex-shrink-0 bg-white/95 border-t border-slate-100 px-4 py-4"
            data-testid="voice-choice-container"
          >
            <div className="max-w-md mx-auto bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="text-center text-slate-700 mb-2 font-medium">
                Perfekt! 🎉
              </p>
              <p className="text-center text-slate-600 text-sm mb-4">
                Jetzt kannst du direkt deine Kontaktdaten eintragen oder uns vorher noch kurz eine Sprachnachricht dalassen.
              </p>
              <p className="text-center text-slate-600 text-sm mb-4 bg-white/60 rounded-lg p-3">
                Fast geschafft 😊<br/>
                Eine kurze Sprachnachricht (z.B. warum wieso weshalb gerade Du) ist bei Bewerbungen immer etwas Besonderes und wird oft bevorzugt angeschaut – dadurch sind deine Chancen oft noch besser.<br/>
                Oder trag direkt deine Kontaktdaten ein.
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => handleVoiceChoice('voice')}
                  className="w-full rounded-xl bg-purple-600 hover:bg-purple-700 text-white py-3"
                  data-testid="voice-choice-voice"
                >
                  🎤 Kurz Sprachnachricht senden
                </Button>
                <Button
                  onClick={() => handleVoiceChoice('contact')}
                  variant="outline"
                  className="w-full rounded-xl border-emerald-400 text-emerald-700 hover:bg-emerald-50 py-3"
                  data-testid="voice-choice-contact"
                >
                  ⌨️ Lieber direkt Kontaktdaten eintragen
                </Button>
              </div>
            </div>
          </motion.div>
        )}
        
        {/* Voice Recorder - Before contact form */}
        {showVoiceRecorder && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex-shrink-0 bg-white/95 border-t border-slate-100 px-4 py-4"
            data-testid="voice-recorder-container"
          >
            <VoiceRecorder
              sessionId={sessionId}
              projectId="hypefactory"
              maxDuration={voiceSettings.max_duration_seconds || 30}
              promptText={voiceSettings.prompt_text}
              onComplete={handleVoiceComplete}
            />
          </motion.div>
        )}
        
        {/* Action Buttons - After Termin confirmed, before submission (ONLY in with_appointment mode) */}
        {showContactButton && !isSubmitted && !showVoiceRecorder && appointmentData.date && appointmentMode === 'with_appointment' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex-shrink-0 bg-gradient-to-t from-white via-white to-white/95 border-t border-slate-100 px-4 py-3"
            data-testid="appointment-buttons-container"
          >
            <div className="flex flex-col gap-2 max-w-md mx-auto">
              <Button
                onClick={openModalWithTermin}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-xl py-4 text-base font-semibold shadow-lg shadow-green-500/30"
                data-testid="appointment-confirm-button"
              >
                ✅ TERMIN BESTÄTIGEN
              </Button>
              <Button
                onClick={handleInfoCheckKontakt}
                className="w-full bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white rounded-xl py-4 text-base font-semibold shadow-lg shadow-blue-500/30"
                data-testid="contact-only-button"
              >
                📞 NUR KONTAKT
              </Button>
            </div>
          </motion.div>
        )}
        
        {/* Contact Only Button - When in contact_only mode OR when no appointment data */}
        {showContactButton && !isSubmitted && !showVoiceRecorder && !showInfoCheckButtons && (appointmentMode === 'contact_only' || !appointmentData.date) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex-shrink-0 bg-gradient-to-t from-white via-white to-white/95 border-t border-slate-100 px-4 py-3"
            data-testid="contact-only-buttons-container"
          >
            <div className="flex flex-col gap-2 max-w-md mx-auto">
              <Button
                onClick={handleInfoCheckKontakt}
                className="w-full bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white rounded-xl py-4 text-base font-semibold shadow-lg shadow-blue-500/30"
                data-testid="contact-button-no-appointment"
              >
                📝 KONTAKTDATEN EINTRAGEN
              </Button>
            </div>
          </motion.div>
        )}

        {/* After submission - hint that questions are welcome */}
        {isSubmitted && !chatClosed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex-shrink-0 bg-white/95 border-t border-slate-100 px-4 py-3"
          >
            <div className="flex flex-col items-center gap-2">
              <p className="text-sm text-slate-500 text-center">
                💬 Du kannst weiterhin Fragen stellen!
              </p>
              <Button
                onClick={handleCloseChat}
                variant="ghost"
                size="sm"
                className="text-slate-400 hover:text-slate-600 text-xs"
                data-testid="close-chat-button"
              >
                Chat schließen ✕
              </Button>
            </div>
          </motion.div>
        )}

        {/* Input Area */}
        <div className="glass border-t border-slate-200/50 p-4 flex-shrink-0">
          {chatClosed ? (
            <div className="text-center py-4">
              <p className="text-slate-500 text-sm mb-3">Chat beendet. Vielen Dank! 🙏</p>
              <Button
                onClick={() => {
                  clearChatSession();
                  window.location.reload();
                }}
                variant="outline"
                size="sm"
                className="text-blue-600 border-blue-300 hover:bg-blue-50"
              >
                Neuen Chat starten
              </Button>
            </div>
          ) : chatMode === 'phone' ? (
            /* Phone Mode - Show phone number to call */
            <div className="text-center py-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Ruf uns an!</h3>
              <p className="text-slate-600 text-sm mb-4">
                Sprich direkt mit unserer KI-Assistentin Lea
              </p>
              {phoneNumber ? (
                <a 
                  href={`tel:${phoneNumber}`}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-4 rounded-full text-lg font-semibold shadow-lg shadow-green-500/30 hover:shadow-xl transition-all"
                >
                  <Phone className="w-5 h-5" />
                  {phoneNumber}
                </a>
              ) : (
                <p className="text-yellow-600 text-sm">
                  Telefonnummer wird noch eingerichtet...
                </p>
              )}
              <p className="text-xs text-slate-400 mt-4">
                Erreichbar Mo-Fr, 9:00-18:00 Uhr
              </p>
            </div>
          ) : chatMode === 'voice' ? (
            /* Voice Chat Mode - Microphone input */
            <div className="py-4">
              <VoiceChatInput
                sessionId={sessionId}
                onResponse={handleVoiceResponse}
                onTranscription={handleVoiceTranscription}
                disabled={isLoading || !sessionId}
              />
              <div className="text-center mt-3 space-y-0.5">
                <p className="text-[10px] text-slate-400 leading-tight">
                  Deine Daten werden vertraulich behandelt.
                </p>
                <p className="text-[10px] text-slate-400 leading-tight">
                  Dieser Chat dient zur ersten Orientierung.
                </p>
                <p className="text-[10px] text-slate-400 leading-tight">
                  <a href="https://www.factoryhype.de/datenschutzerklaerung/" target="_blank" rel="noopener noreferrer" className="hover:underline">Datenschutz</a>
                  {" | "}
                  <a href="https://www.factoryhype.de/impressum/" target="_blank" rel="noopener noreferrer" className="hover:underline">Impressum</a>
                </p>
              </div>
            </div>
          ) : (
            /* Text Chat Mode - Default */
            <>
              <div className="flex gap-3" data-testid="chat-form">
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder={isSubmitted ? "Noch eine Frage? Schreib einfach..." : "Schreibe eine Nachricht..."}
                  disabled={isLoading}
                  className="flex-1 rounded-full border-slate-200 bg-white px-5 py-6 text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 input-focus-ring"
                  data-testid="chat-input"
                />
                <Button
                  type="button"
                  disabled={!inputValue.trim() || isLoading}
                  onClick={() => sendMessage()}
                  className="rounded-full w-12 h-12 bg-gradient-to-br from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 shadow-lg shadow-blue-500/30 btn-primary-hover p-0"
                  data-testid="send-button"
                >
                  <Send className="w-5 h-5" />
                </Button>
              </div>
              <div className="text-center mt-2 space-y-0.5">
                <p className="text-[10px] text-slate-400 leading-tight">
                  Deine Daten werden vertraulich behandelt.
                </p>
                <p className="text-[10px] text-slate-400 leading-tight">
                  Dieser Chat dient zur ersten Orientierung.
                </p>
                <p className="text-[10px] text-slate-400 leading-tight">
                  <a href="https://www.factoryhype.de/datenschutzerklaerung/" target="_blank" rel="noopener noreferrer" className="hover:underline">Datenschutz</a>
                  {" | "}
                  <a href="https://www.factoryhype.de/impressum/" target="_blank" rel="noopener noreferrer" className="hover:underline">Impressum</a>
                </p>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Slot Selection Modal */}
      <SlotSelectionModal
        slots={availableSlots}
        isOpen={isSlotModalOpen}
        onClose={() => setIsSlotModalOpen(false)}
        selectedSlot={selectedSlot}
        onSelect={setSelectedSlot}
        onConfirm={handleSlotConfirm}
      />

      {/* Contact Modal */}
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        onSuccess={handleContactSuccess}
        sessionId={sessionId}
        appointmentData={modalWithAppointment ? appointmentData : { date: null, time: null }}
      />
    </div>
  );
}
